import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from 'reactstrap';

import {
  type CigarEmbeddingCoverage,
  getCigarEmbeddingCoverage,
} from '@/api/cigar-matching';

import { humanizeEmbeddingKey } from './embedding-display-utils';

export const normalizeCoverageCigarIds = (cigarIds: Array<number | string>) =>
  Array.from(
    new Set(
      cigarIds
        .map(Number)
        .filter((cigarId) => Number.isInteger(cigarId) && cigarId > 0)
    )
  ).slice(0, 500);

export const getCoverageTestingAvailability = (
  coverage: CigarEmbeddingCoverage | undefined,
  hasTestImages: boolean
) => ({
  canFindSimilar: hasTestImages,
  canIdentify: hasTestImages,
  targetMissingEmbeddings: coverage?.has_embeddings === false,
});

export const useCigarEmbeddingCoverage = (
  cigarIds: Array<number | string>,
  refreshToken = 0
) => {
  const queryKey = normalizeCoverageCigarIds(cigarIds)
    .sort((left, right) => left - right)
    .join(',');
  const normalizedIds = useMemo(
    () => (queryKey ? queryKey.split(',').map(Number) : []),
    [queryKey]
  );
  const [coverageByCigarId, setCoverageByCigarId] = useState<
    Record<number, CigarEmbeddingCoverage>
  >({});
  const [model, setModel] = useState('');
  const [version, setVersion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    const nextRequestId = requestId.current + 1;
    requestId.current = nextRequestId;

    if (!normalizedIds.length) {
      setCoverageByCigarId({});
      setModel('');
      setVersion('');
      setIsLoading(false);
      setIsUnavailable(false);
      return undefined;
    }

    setCoverageByCigarId({});
    setIsLoading(true);
    setIsUnavailable(false);

    getCigarEmbeddingCoverage(normalizedIds)
      .then((response) => {
        if (requestId.current !== nextRequestId) return;
        setCoverageByCigarId(
          Object.fromEntries(
            (response.cigars || []).map((coverage) => [
              coverage.cigar_id,
              coverage,
            ])
          )
        );
        setModel(response.model || '');
        setVersion(response.version || '');
      })
      .catch((error) => {
        if (requestId.current !== nextRequestId) return;
        console.error('Unable to load cigar embedding coverage', error);
        setCoverageByCigarId({});
        setModel('');
        setVersion('');
        setIsUnavailable(true);
      })
      .finally(() => {
        if (requestId.current === nextRequestId) setIsLoading(false);
      });

    return () => {
      if (requestId.current === nextRequestId) requestId.current += 1;
    };
  }, [queryKey, refreshToken]);

  return {
    coverageByCigarId,
    model,
    version,
    isLoading,
    isUnavailable,
  };
};

const formatCoverageDate = (value?: string | null) => {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

export function EmbeddingCoverageCell({
  coverage,
  model,
  version,
  isLoading,
  isUnavailable,
}: {
  coverage?: CigarEmbeddingCoverage;
  model?: string;
  version?: string;
  isLoading?: boolean;
  isUnavailable?: boolean;
}) {
  if (isLoading) {
    return <span className="recognition-coverage-muted">Checking…</span>;
  }
  if (isUnavailable || !coverage) {
    return <span className="recognition-coverage-muted">Unavailable</span>;
  }

  const sourceCounts = Object.entries(coverage.source_counts || {});
  const details = [
    ...sourceCounts.map(
      ([source, count]) =>
        `${humanizeEmbeddingKey(source)}: ${Number(count).toLocaleString()}`
    ),
    model ? `Model: ${model}` : null,
    version ? `Version: ${version}` : null,
    `Last updated: ${formatCoverageDate(coverage.last_updated_at)}`,
  ]
    .filter(Boolean)
    .join('\n');

  if (!coverage.has_embeddings) {
    return (
      <div
        className="recognition-coverage recognition-coverage-empty"
        title={details}
      >
        <strong>No embeddings</strong>
        <span>Not searchable visually</span>
      </div>
    );
  }

  return (
    <div className="recognition-coverage" title={details}>
      <Badge color="success">Ready</Badge>
      <strong>
        {coverage.reference_image_count.toLocaleString()}{' '}
        {coverage.reference_image_count === 1 ? 'reference' : 'references'}
      </strong>
      <span>{coverage.embedding_count.toLocaleString()} vectors</span>
    </div>
  );
}
